import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { message, Modal } from 'antd'
import {
  createSiteEntry,
  getSiteEntry,
  listSiteTaxonomies,
  listTaxonomyTerms,
  rotateSiteAsset,
  searchSiteEntries,
  siteAssetVariantUrl,
  updateSiteEntry,
  uploadSiteAsset,
} from '../../api'
import type { ImageAssetPickerSelection, SaasApiError } from '../../api'
import { ImageAssetPicker } from '../../components/ImageAssetPicker'
import { VistoeraActionConfirm } from '../../ui/action'
import type { Taxonomy, TaxonomyTerm } from '../../types'
import { rotateImageFile } from './rotateImageFile'
import './item-editor.css'

/**
 * Item Editor V2 — metadata-first save, async media, sticky actions,
 * optional category, canonical image rotation.
 *
 * Architecture (Vistoera):
 *   ContentType "item" + ContentEntry (fields_json); Category/Topic =
 *   generic Taxonomy. "保存草稿" = status=draft; "保存并发布" =
 *   status=published (Snapshot publish is separate). Media uses SiteAsset
 *   + DeepZoom Job — never wait for DeepZoom in the save button.
 */

type ImageUploadStatus = 'pending' | 'uploading' | 'uploaded' | 'processing' | 'ready' | 'failed'

interface GalleryImage {
  key: string
  assetId?: string
  pickerItemId?: string
  previewUrl: string
  file?: File
  isPrimary: boolean
  originalFilename?: string
  uploadStatus: ImageUploadStatus
  uploadError?: string
  rotationQuarters: number
}

/** ONE_VARIANT_PER_CHAIN for library-projected picker rows (`lib:{chain}:{kind}`). */
function familyKeyFromPickerId(pickerItemId: string): string | null {
  if (!pickerItemId.startsWith('lib:')) return null
  const parts = pickerItemId.split(':')
  return parts.length >= 2 ? `lib:${parts[1]}` : null
}

const CATEGORY_ACCESSION_CODE: Record<string, string> = {
  中国书画: 'SH',
  油画: 'YH',
  古籍善本: 'SB',
  手稿与文献: 'WX',
  瓷器: 'CQ',
  玉器: 'YQ',
  青铜器: 'QT',
  家具: 'JJ',
  摄影: 'SY',
  钱币: 'QB',
  其他: 'QY',
}

let localKeySeq = 0
function nextLocalKey(): string {
  localKeySeq += 1
  return `local-${Date.now()}-${localKeySeq}`
}

interface BasicInfoState {
  accession_number: string
  title_zh: string
  title_original: string
  creator_display: string
  creation_period_display: string
  region: string
  material_summary: string
  dimension_summary: string
  short_summary: string
  description: string
  research_notes: string
}

const EMPTY_BASIC_INFO: BasicInfoState = {
  accession_number: '',
  title_zh: '',
  title_original: '',
  creator_display: '',
  creation_period_display: '',
  region: '',
  material_summary: '',
  dimension_summary: '',
  short_summary: '',
  description: '',
  research_notes: '',
}

function classifySaveError(err: unknown, phase: 'upload' | 'save'): string {
  const apiErr = err as SaasApiError
  const msg = apiErr?.message || ''
  if (apiErr?.status === 401) return '登录已失效，请重新登录'
  if (phase === 'upload') {
    if (!apiErr?.status) return msg || '上传失败：网络连接失败'
    if (apiErr.status >= 500) return '上传失败：服务器错误'
    return `上传失败：${msg || '请重试'}`
  }
  if (!apiErr?.status) return msg || '保存失败：网络连接失败'
  if (apiErr.status >= 500) return '保存失败：服务器错误'
  return `保存失败：${msg || '请重试'}`
}

export default function ItemEditorPage({
  workspaceId,
  siteId,
  siteBase,
}: {
  workspaceId: string
  siteId: string
  siteBase: string
}) {
  const { itemId: itemIdParam } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(['website', 'actions'])
  const [entryId, setEntryId] = useState<string | undefined>(itemIdParam)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  const [, setCategoryTaxonomyId] = useState<string | null>(null)
  const [, setTopicTaxonomyId] = useState<string | null>(null)
  const [categoryTerms, setCategoryTerms] = useState<TaxonomyTerm[]>([])
  const [topicTerms, setTopicTerms] = useState<TaxonomyTerm[]>([])

  const [categoryId, setCategoryId] = useState<string>('')
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([])
  const [pendingTermIds, setPendingTermIds] = useState<string[] | null>(null)
  const [basic, setBasic] = useState<BasicInfoState>(EMPTY_BASIC_INFO)
  const [frontendVisible, setFrontendVisible] = useState(true)
  const [images, setImages] = useState<GalleryImage[]>([])
  const [status, setStatus] = useState<string>('draft')

  const [suggesting, setSuggesting] = useState(false)
  const [busy, setBusy] = useState<'draft' | 'publish' | null>(null)
  const [hint, setHint] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)
  const [editorDirty, setEditorDirty] = useState(false)
  const [assetPickerOpen, setAssetPickerOpen] = useState(false)
  const dragSrcIndexRef = useRef<number | null>(null)

  const pendingObjectUrls = useRef<Set<string>>(new Set())
  const imagesRef = useRef(images)
  imagesRef.current = images

  const uploadingCount = images.filter((im) => im.uploadStatus === 'uploading').length
  const processingCount = images.filter(
    (im) => im.uploadStatus === 'processing' || (im.assetId && im.uploadStatus === 'uploaded'),
  ).length
  const failedUploadCount = images.filter((im) => im.uploadStatus === 'failed').length

  useEffect(() => {
    listSiteTaxonomies(workspaceId, siteId).then((r) => {
      const category = r.data.find((t: Taxonomy) => t.key === 'category')
      const topic = r.data.find((t: Taxonomy) => t.key === 'topic')
      if (category) {
        setCategoryTaxonomyId(category.id)
        listTaxonomyTerms(workspaceId, siteId, category.id).then((tr) => setCategoryTerms(tr.data))
      }
      if (topic) {
        setTopicTaxonomyId(topic.id)
        listTaxonomyTerms(workspaceId, siteId, topic.id).then((tr) => setTopicTerms(tr.data))
      }
    })
  }, [workspaceId, siteId])

  useEffect(() => {
    if (!itemIdParam) {
      setLoading(false)
      return
    }
    setLoading(true)
    getSiteEntry(workspaceId, siteId, itemIdParam)
      .then((r) => {
        const entry = r.data
        setEntryId(entry.id)
        setStatus(entry.status)
        setFrontendVisible(entry.visibility !== 'private')
        const f = entry.fields || {}
        setBasic({
          accession_number: String(f.accession_number ?? ''),
          title_zh: String(f.title_zh ?? entry.label ?? ''),
          title_original: String(f.title_original ?? ''),
          creator_display: String(f.creator_display ?? ''),
          creation_period_display: String(f.creation_period_display ?? ''),
          region: String(f.region ?? ''),
          material_summary: String(f.material_summary ?? ''),
          dimension_summary: String(f.dimension_summary ?? ''),
          short_summary: String(f.short_summary ?? ''),
          description: String(f.description ?? ''),
          research_notes: String(f.research_notes ?? ''),
        })
        const galleryRaw = Array.isArray(f.gallery_images) ? (f.gallery_images as Array<Record<string, unknown>>) : []
        const gallery: GalleryImage[] = galleryRaw.map((g) => ({
          key: nextLocalKey(),
          assetId: String(g.asset_id ?? ''),
          previewUrl: siteAssetVariantUrl(workspaceId, String(g.asset_id ?? ''), 'thumbnail') + `?t=${Date.now()}`,
          isPrimary: !!g.is_primary,
          originalFilename: g.caption ? String(g.caption) : undefined,
          uploadStatus: 'ready' as const,
          rotationQuarters: 0,
        }))
        if (entry.cover_asset_id && !gallery.some((im) => im.isPrimary)) {
          const match = gallery.find((im) => im.assetId === entry.cover_asset_id)
          if (match) match.isPrimary = true
        }
        if (gallery.length === 0 && entry.cover_asset_id) {
          gallery.push({
            key: nextLocalKey(),
            assetId: entry.cover_asset_id,
            previewUrl: siteAssetVariantUrl(workspaceId, entry.cover_asset_id, 'thumbnail') + `?t=${Date.now()}`,
            isPrimary: true,
            uploadStatus: 'ready',
            rotationQuarters: 0,
          })
        }
        if (gallery.length > 0 && !gallery.some((im) => im.isPrimary)) gallery[0].isPrimary = true
        setImages(gallery)
        setPendingTermIds(entry.taxonomy_term_ids ?? [])
        setEditorDirty(false)
      })
      .catch((err) => {
        const apiErr = err as SaasApiError
        if (apiErr.status === 404) setNotFound(true)
        else setError(apiErr.message)
      })
      .finally(() => setLoading(false))
  }, [workspaceId, siteId, itemIdParam])

  useEffect(() => {
    return () => {
      pendingObjectUrls.current.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])

  useEffect(() => {
    if (pendingTermIds === null) return
    if (categoryTerms.length === 0 && topicTerms.length === 0) return
    const categoryIdSet = new Set(categoryTerms.map((t) => t.id))
    const topicIdSet = new Set(topicTerms.map((t) => t.id))
    const foundCategory = pendingTermIds.find((id) => categoryIdSet.has(id))
    if (foundCategory) setCategoryId(foundCategory)
    setSelectedTopicIds(pendingTermIds.filter((id) => topicIdSet.has(id)))
    setPendingTermIds(null)
  }, [pendingTermIds, categoryTerms, topicTerms])

  // Leave-page guard: only when form text/metadata is dirty.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!editorDirty) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [editorDirty])

  const categoryTermById = useMemo(() => new Map(categoryTerms.map((t) => [t.id, t])), [categoryTerms])
  const selectedCategoryTerm = categoryId ? categoryTermById.get(categoryId) : undefined

  const markDirty = useCallback(() => {
    setEditorDirty(true)
    setSavedFlash(false)
  }, [])

  const openImagePicker = useCallback(() => setAssetPickerOpen(true), [])

  const addFromAssetPicker = useCallback(
    (selections: ImageAssetPickerSelection[]) => {
      if (!selections.length) return
      setImages((prev) => {
        const existingAssetIds = new Set(prev.map((im) => im.assetId).filter(Boolean))
        const existingFamilies = new Set(
          prev.map((im) => (im.pickerItemId ? familyKeyFromPickerId(im.pickerItemId) : null)).filter(Boolean) as string[],
        )
        const additions: GalleryImage[] = selections
          .filter((sel) => {
            if (existingAssetIds.has(sel.assetId)) return false
            const family = familyKeyFromPickerId(sel.pickerItemId)
            if (family && existingFamilies.has(family)) return false
            return true
          })
          .map((sel) => ({
            key: nextLocalKey(),
            assetId: sel.assetId,
            pickerItemId: sel.pickerItemId,
            previewUrl: siteAssetVariantUrl(workspaceId, sel.assetId, 'thumbnail'),
            isPrimary: false,
            uploadStatus: 'ready' as const,
            rotationQuarters: 0,
          }))
        if (!additions.length) return prev
        const next = [...prev, ...additions]
        if (!next.some((im) => im.isPrimary) && next.length) next[0] = { ...next[0], isPrimary: true }
        return next
      })
      markDirty()
      setAssetPickerOpen(false)
    },
    [markDirty, workspaceId],
  )

  const setPrimary = (key: string) => {
    setImages((prev) => prev.map((im) => ({ ...im, isPrimary: im.key === key })))
    markDirty()
  }

  // VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §13/§16 — icon-triggered
  // lightweight delete confirmation. Confirmation UI is the anchored
  // `VistoeraActionConfirm` wrapping the trigger button in JSX; this
  // function only performs the (synchronous, local-state) removal.
  const removeImage = (key: string) => {
    setImages((prev) => {
      const removed = prev.find((im) => im.key === key)
      if (removed && removed.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(removed.previewUrl)
        pendingObjectUrls.current.delete(removed.previewUrl)
      }
      const next = prev.filter((im) => im.key !== key)
      if (removed?.isPrimary && next.length) next[0] = { ...next[0], isPrimary: true }
      return next
    })
    markDirty()
  }

  const rotateImage = async (key: string) => {
    const target = images.find((im) => im.key === key)
    if (!target) return
    try {
      if (target.file && !target.assetId) {
        const rotated = await rotateImageFile(target.file, 1)
        const url = URL.createObjectURL(rotated)
        if (target.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(target.previewUrl)
          pendingObjectUrls.current.delete(target.previewUrl)
        }
        pendingObjectUrls.current.add(url)
        setImages((prev) =>
          prev.map((im) =>
            im.key === key
              ? {
                  ...im,
                  file: rotated,
                  previewUrl: url,
                  originalFilename: rotated.name,
                  rotationQuarters: (im.rotationQuarters + 1) % 4,
                  uploadStatus: 'pending',
                  uploadError: undefined,
                }
              : im,
          ),
        )
        markDirty()
        return
      }
      if (target.assetId) {
        setHint('正在旋转图片…')
        await rotateSiteAsset(workspaceId, target.assetId, 1)
        const bust = `?t=${Date.now()}`
        setImages((prev) =>
          prev.map((im) =>
            im.key === key
              ? {
                  ...im,
                  previewUrl: siteAssetVariantUrl(workspaceId, target.assetId!, 'thumbnail') + bust,
                  rotationQuarters: (im.rotationQuarters + 1) % 4,
                  uploadStatus: 'processing',
                }
              : im,
          ),
        )
        setHint('已旋转 · 图片处理中')
        message.success('已旋转，后台重新生成切片')
      }
    } catch (err) {
      message.error(classifySaveError(err, 'upload'))
    }
  }

  const onCardDragStart = (index: number) => (e: React.DragEvent) => {
    dragSrcIndexRef.current = index
    e.dataTransfer.effectAllowed = 'move'
  }
  const onCardDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault()
    if (dragSrcIndexRef.current === null || dragSrcIndexRef.current === index) return
  }
  const onCardDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault()
    const from = dragSrcIndexRef.current
    dragSrcIndexRef.current = null
    if (from === null || from === index) return
    setImages((prev) => {
      const next = prev.slice()
      const [moved] = next.splice(from, 1)
      next.splice(index, 0, moved)
      return next
    })
    markDirty()
  }

  const toggleTopic = (id: string) => {
    setSelectedTopicIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    markDirty()
  }

  const onSuggestAccession = async () => {
    // Category is optional — auto-numbering must not force it. With a
    // category selected, use its prefix code; otherwise fall back to a
    // generic "ITEM" prefix scoped to the whole site.
    const code = selectedCategoryTerm
      ? CATEGORY_ACCESSION_CODE[selectedCategoryTerm.name] ?? selectedCategoryTerm.name.slice(0, 2).toUpperCase()
      : 'ITEM'
    setSuggesting(true)
    try {
      const { data } = await searchSiteEntries(workspaceId, siteId, {
        content_type: 'item',
        ...(categoryId ? { taxonomy_term_ids: [categoryId] } : {}),
        page: 1,
        page_size: 200,
      })
      const pattern = new RegExp(`^${code}(\\d+)$`, 'i')
      let maxNum = 0
      for (const entry of data.items) {
        const acc = String(entry.fields.accession_number ?? '')
        const m = pattern.exec(acc)
        if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10))
      }
      const suggestion = `${code}${String(maxNum + 1).padStart(3, '0')}`
      setBasic((prev) => ({ ...prev, accession_number: suggestion }))
      markDirty()
    } catch (err) {
      message.error((err as SaasApiError).message || '获取编号失败')
    } finally {
      setSuggesting(false)
    }
  }

  const buildGalleryFields = (list: GalleryImage[]) => {
    const galleryImages = list
      .filter((im) => im.assetId)
      .map((im, idx) => ({ asset_id: im.assetId, is_primary: im.isPrimary, sort_order: idx }))
    const primary = list.find((im) => im.isPrimary && im.assetId) ?? list.find((im) => im.assetId)
    return { galleryImages, coverAssetId: primary?.assetId ?? null }
  }

  const persistEntry = async (
    targetStatus: 'draft' | 'published',
    list: GalleryImage[],
    existingId: string | undefined,
  ) => {
    const { galleryImages, coverAssetId } = buildGalleryFields(list)
    // ITEM-DETAIL-INITIAL-IMAGE-RACE-P0: real, recomputed-every-call count
    // of images this browser still has to upload (queued or actively
    // uploading — never a permanently-`failed` one, which has already
    // settled). Read by the public Item Detail page
    // (`public_site.py::_image_pending_hint`) to tell "an upload for this
    // exact item is genuinely still in flight" apart from "this item has
    // no image at all" — the two states the old empty-gallery check could
    // not tell apart. Always reaches 0 once every queued upload settles
    // (success or permanent failure), so it never gets stuck signalling
    // "pending" for an item that has, in fact, finished (or given up).
    const pendingImageUploads = list.filter((im) => im.file && !im.assetId && im.uploadStatus !== 'failed').length
    const fields = { ...basic, gallery_images: galleryImages, pending_image_uploads: pendingImageUploads }
    const taxonomyTermIds = [...(categoryId ? [categoryId] : []), ...selectedTopicIds]
    const visibility = frontendVisible ? 'public' : 'private'

    if (existingId) {
      await updateSiteEntry(workspaceId, siteId, existingId, {
        label: basic.title_zh.trim(),
        fields,
        taxonomy_term_ids: taxonomyTermIds,
        cover_asset_id: coverAssetId,
        status: targetStatus,
        visibility,
      })
      return existingId
    }
    const slugBase =
      basic.accession_number.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') ||
      `item-${Date.now()}`
    const { data: created } = await createSiteEntry(workspaceId, siteId, {
      content_type: 'item',
      slug: slugBase,
      label: basic.title_zh.trim(),
      fields,
      taxonomy_term_ids: taxonomyTermIds,
      cover_asset_id: coverAssetId,
      status: targetStatus,
      visibility,
    })
    return created.id
  }

  const uploadOneImage = async (im: GalleryImage): Promise<GalleryImage> => {
    if (im.assetId || !im.file) return im
    setImages((prev) =>
      prev.map((x) => (x.key === im.key ? { ...x, uploadStatus: 'uploading', uploadError: undefined } : x)),
    )
    try {
      const { data: asset } = await uploadSiteAsset(workspaceId, siteId, im.file)
      const next: GalleryImage = {
        ...im,
        assetId: asset.id,
        file: undefined,
        uploadStatus: 'processing',
        uploadError: undefined,
        previewUrl: siteAssetVariantUrl(workspaceId, asset.id, 'thumbnail') + `?t=${Date.now()}`,
      }
      if (im.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(im.previewUrl)
        pendingObjectUrls.current.delete(im.previewUrl)
      }
      setImages((prev) => prev.map((x) => (x.key === im.key ? next : x)))
      return next
    } catch (err) {
      const msg = classifySaveError(err, 'upload')
      setImages((prev) =>
        prev.map((x) => (x.key === im.key ? { ...x, uploadStatus: 'failed', uploadError: msg } : x)),
      )
      throw Object.assign(new Error(msg), { imageKey: im.key })
    }
  }

  const buildFieldsAndSave = async (targetStatus: 'draft' | 'published') => {
    if (!basic.title_zh.trim()) {
      message.error('请填写中文名称')
      return
    }
    if (!basic.accession_number.trim()) {
      message.error('请填写条目编号')
      return
    }

    // Publish requires original upload confirmed when images were selected.
    const pendingFiles = images.filter((im) => im.file && !im.assetId)
    if (targetStatus === 'published' && pendingFiles.length > 0) {
      // Will upload below before flipping to published; if upload fails, keep draft.
    }

    setBusy(targetStatus === 'draft' ? 'draft' : 'publish')
    setHint(targetStatus === 'draft' ? '正在保存条目…' : '正在保存并发布…')

    let currentId = entryId
    let working = [...images]

    try {
      // 1) Metadata first — Item exists even if later uploads fail.
      currentId = await persistEntry(
        targetStatus === 'published' && pendingFiles.length > 0 ? 'draft' : targetStatus,
        working,
        currentId,
      )
      if (!entryId) {
        setEntryId(currentId)
        navigate(`${siteBase}/items/${currentId}/edit`, { replace: true })
      }
      setEditorDirty(false)
      setSavedFlash(true)

      // 2) Upload originals sequentially (avoid Preview 502 from parallel large PUTs).
      let uploadFailures = 0
      for (const im of working) {
        if (!im.file || im.assetId) continue
        setHint(`正在上传原图… (${im.originalFilename || 'image'})`)
        try {
          const uploaded = await uploadOneImage(im)
          working = working.map((x) => (x.key === im.key ? uploaded : x))
          // Patch gallery after each successful original accept.
          await persistEntry(
            targetStatus === 'published' ? 'draft' : targetStatus,
            working,
            currentId,
          )
        } catch {
          uploadFailures += 1
        }
      }

      // 3) If publish requested and all selected originals accepted, flip status.
      const stillPending = working.some((im) => im.file && !im.assetId)
      const hasAnyAsset = working.some((im) => im.assetId)
      if (targetStatus === 'published') {
        if (stillPending && working.some((im) => im.uploadStatus === 'failed' || im.file)) {
          await persistEntry('draft', working, currentId)
          setStatus('draft')
          message.warning('条目已保存为草稿；部分图片未上传完成，暂不发布（避免断图）')
        } else if (!working.length || hasAnyAsset || working.every((im) => !im.file)) {
          // Allow publish with zero images, or when all images have originals uploaded.
          // DeepZoom may still be processing — Public Renderer falls back to original.
          await persistEntry('published', working, currentId)
          setStatus('published')
          message.success(
            uploadFailures
              ? `已发布；${uploadFailures} 张图片上传失败`
              : processingCount || working.some((im) => im.uploadStatus === 'processing')
                ? '已发布 · 图片处理中'
                : '已保存并发布',
          )
        }
      } else {
        setStatus('draft')
        if (uploadFailures) {
          message.warning(`条目已保存，${uploadFailures} 张图片上传失败`)
        } else if (working.some((im) => im.uploadStatus === 'processing')) {
          message.success('条目已保存，图片正在后台处理')
        } else {
          message.success('草稿已保存')
        }
      }

      const proc = working.filter((im) => im.uploadStatus === 'processing').length
      setHint(
        proc > 0
          ? `已保存 · ${proc} 张图片处理中`
          : uploadFailures
            ? `已保存 · ${uploadFailures} 张失败`
            : '已保存',
      )
    } catch (err) {
      message.error(classifySaveError(err, 'save'))
      setHint('')
    } finally {
      setBusy(null)
    }
  }

  const onNavigateBack = () => {
    if (editorDirty) {
      Modal.confirm({
        title: '有未保存的修改',
        content: '你有未保存的修改，确定离开？',
        okText: '离开',
        cancelText: '继续编辑',
        onOk: () => navigate(`${siteBase}/items`),
      })
      return
    }
    navigate(`${siteBase}/items`)
  }

  const statusLabel = useMemo(() => {
    if (busy) return busy === 'draft' ? '保存中…' : '发布中…'
    if (uploadingCount > 0) return `上传中 · ${uploadingCount}`
    if (savedFlash && failedUploadCount > 0) return `已保存 · ${failedUploadCount} 张失败`
    if (savedFlash && processingCount > 0) return `已保存 · ${processingCount} 张图片处理中`
    if (savedFlash) return '已保存'
    if (editorDirty) return '未保存修改'
    if (entryId) return status === 'published' ? '已发布' : '草稿'
    return '新建条目'
  }, [busy, uploadingCount, savedFlash, processingCount, failedUploadCount, editorDirty, entryId, status])

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>加载中…</div>
  }
  if (notFound) {
    return <div style={{ padding: 40, textAlign: 'center' }}>该条目不存在或已被删除。</div>
  }
  if (error) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#d43030' }}>{error}</div>
  }

  const imageStatusText = (im: GalleryImage) => {
    switch (im.uploadStatus) {
      case 'pending':
        return '待上传'
      case 'uploading':
        return '上传中'
      case 'uploaded':
      case 'processing':
        return '处理中'
      case 'ready':
        return '就绪'
      case 'failed':
        return im.uploadError || '失败'
      default:
        return ''
    }
  }

  return (
    <div className="lc-item-editor">
      <div className="edit-action-bar">
        <div className="edit-action-bar-left">
          <h2 className="edit-action-title">{entryId ? '编辑条目' : '新建条目'}</h2>
          <span className="editor-actions-hint">{hint || statusLabel}</span>
        </div>
        <div className="edit-action-bar-right">
          <a onClick={onNavigateBack} style={{ cursor: 'pointer', marginRight: 8 }}>
            ← 返回列表
          </a>
          <button type="button" className="btn" disabled={busy !== null} onClick={() => buildFieldsAndSave('draft')}>
            {busy === 'draft' ? '保存中…' : '保存草稿'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy !== null}
            onClick={() => buildFieldsAndSave('published')}
            style={{ background: 'var(--lc-primary)', borderColor: 'var(--lc-primary)', color: '#fff' }}
          >
            {busy === 'publish' ? '发布中…' : '保存并发布'}
          </button>
        </div>
      </div>

      {/* ① 图片 — all selection goes through Unified Image Asset Picker */}
      <div className="form-section">
        <div className="form-section-header">
          <span>图片</span>
          {images.length > 0 && (
            <button type="button" className="btn btn-secondary" style={{ marginLeft: 12, fontSize: 12 }} onClick={openImagePicker}>
              选择图片
            </button>
          )}
        </div>
        <div className="form-section-body">
          <div className={`img-mgr-area${images.length ? ' has-images' : ''}`}>
            {images.length === 0 ? (
              <div className="img-mgr-empty">
                <span className="img-mgr-empty-text">暂无图片</span>
                <button type="button" className="btn btn-primary" onClick={openImagePicker}>
                  选择图片
                </button>
              </div>
            ) : (
              <div className="img-mgr-grid">
                {images.map((im, index) => (
                  <div
                    key={im.key}
                    className="img-mgr-cell"
                    draggable
                    onDragStart={onCardDragStart(index)}
                    onDragOver={onCardDragOver(index)}
                    onDrop={onCardDrop(index)}
                  >
                    <div className="img-mgr-card">
                      <div className="img-mgr-thumb">
                        <span className="img-mgr-num">{index + 1}</span>
                        {im.isPrimary && <span className="img-mgr-primary-badge">主图</span>}
                        <img src={im.previewUrl} alt="" />
                        {im.uploadStatus !== 'ready' && (
                          <span className={`img-mgr-status img-mgr-status-${im.uploadStatus}`}>{imageStatusText(im)}</span>
                        )}
                      </div>
                      <div className="img-mgr-filename" title={im.originalFilename}>
                        {im.originalFilename || (im.assetId ? im.assetId.slice(0, 8) : '')}
                      </div>
                      <div className="img-mgr-actions">
                        <button
                          type="button"
                          className="img-mgr-action"
                          title="顺时针旋转 90°"
                          onClick={() => rotateImage(im.key)}
                          disabled={!im.assetId || im.uploadStatus === 'uploading'}
                        >
                          ↻ 旋转
                        </button>
                        <button
                          type="button"
                          className={`img-mgr-action${im.isPrimary ? ' is-active' : ''}`}
                          title="设为主图"
                          onClick={() => setPrimary(im.key)}
                        >
                          {im.isPrimary ? '★' : '☆'}
                        </button>
                        <VistoeraActionConfirm
                          title={t('website:deleteImage.confirmTitle')}
                          description={t('website:deleteImage.confirmDescription')}
                          confirmLabel={t('actions:delete')}
                          cancelLabel={t('actions:cancel')}
                          onConfirm={() => removeImage(im.key)}
                        >
                          <button type="button" className="img-mgr-action is-danger" title={t('website:deleteImage.buttonTitle')}>
                            {t('actions:delete')}
                          </button>
                        </VistoeraActionConfirm>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="img-mgr-cell img-mgr-add" onClick={openImagePicker} title="添加图片">
                  <span className="img-mgr-add-plus">+</span>
                  <span className="img-mgr-add-label">添加图片</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ② 条目分类 */}
      <div className="form-section">
        <div className="form-section-header">
          <span>条目分类</span>
        </div>
        <div className="form-section-body">
          <div className="form-grid">
            <div className="form-field span-2">
              <label>分类</label>
              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value)
                  markDirty()
                }}
              >
                <option value="">不选择</option>
                {categoryTerms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ③ 主题分类 */}
      <div className="form-section">
        <div className="form-section-header">
          <span>主题分类</span>
        </div>
        <div className="form-section-body">
          <div className="theme-tag-list">
            {topicTerms.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`theme-tag${selectedTopicIds.includes(t.id) ? ' selected' : ''}`}
                onClick={() => toggleTopic(t.id)}
              >
                {t.name}
              </button>
            ))}
            {topicTerms.length === 0 && (
              <span style={{ color: 'var(--lc-text-muted)', fontSize: 13 }}>暂无主题分类，请先在「主题分类管理」中添加。</span>
            )}
          </div>
        </div>
      </div>

      {/* ④ 基本信息 */}
      <div className="form-section">
        <div className="form-section-header">
          <span>基本信息</span>
        </div>
        <div className="form-section-body">
          <div className="form-grid">
            <div className="form-field">
              <label>
                条目编号<span className="req">*</span>
              </label>
              <div className="field-inline">
                <input
                  type="text"
                  value={basic.accession_number}
                  onChange={(e) => {
                    setBasic((p) => ({ ...p, accession_number: e.target.value }))
                    markDirty()
                  }}
                />
                <button type="button" className="btn" onClick={onSuggestAccession} disabled={suggesting} title="根据分类自动生成">
                  {suggesting ? '生成中…' : '自动编号'}
                </button>
              </div>
            </div>
            <div className="form-field span-2">
              <label>
                中文名称<span className="req">*</span>
              </label>
              <input
                type="text"
                value={basic.title_zh}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, title_zh: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field span-2">
              <label>英文/原文名称</label>
              <input
                type="text"
                value={basic.title_original}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, title_original: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field">
              <label>作者/制造者</label>
              <input
                type="text"
                value={basic.creator_display}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, creator_display: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field">
              <label>年代</label>
              <input
                type="text"
                value={basic.creation_period_display}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, creation_period_display: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field">
              <label>国家/地区</label>
              <input
                type="text"
                value={basic.region}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, region: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field">
              <label>材质概述</label>
              <input
                type="text"
                value={basic.material_summary}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, material_summary: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field">
              <label>尺寸概述</label>
              <input
                type="text"
                value={basic.dimension_summary}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, dimension_summary: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field span-3">
              <label>一句话摘要</label>
              <textarea
                rows={2}
                value={basic.short_summary}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, short_summary: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field span-3">
              <label>条目简介</label>
              <textarea
                rows={4}
                value={basic.description}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, description: e.target.value }))
                  markDirty()
                }}
              />
            </div>
            <div className="form-field span-3">
              <label>研究备注</label>
              <textarea
                rows={3}
                value={basic.research_notes}
                onChange={(e) => {
                  setBasic((p) => ({ ...p, research_notes: e.target.value }))
                  markDirty()
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ⑤ 发布与权限 */}
      <div className="form-section">
        <div className="form-section-header">
          <span>发布与权限</span>
        </div>
        <div className="form-section-body">
          <div className="form-grid">
            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={frontendVisible}
                  onChange={(e) => {
                    setFrontendVisible(e.target.checked)
                    markDirty()
                  }}
                  style={{ width: 'auto' }}
                />
                前端展示
              </label>
            </div>
            <div className="form-field">
              <label>当前状态</label>
              <span style={{ fontSize: 13.5 }}>{status === 'published' ? '已发布' : status === 'archived' ? '已归档' : '草稿'}</span>
            </div>
          </div>
        </div>
      </div>

      <ImageAssetPicker
        open={assetPickerOpen}
        workspaceId={workspaceId}
        siteId={siteId}
        purpose="item_image"
        selectionMode="multiple"
        maxSelection={20}
        title="选择条目图片"
        initialSelection={[]}
        onCancel={() => setAssetPickerOpen(false)}
        onConfirm={addFromAssetPicker}
      />
    </div>
  )
}
